/* global FormData */
/* global File */

import {
  GET_MANY,
  GET_MANY_REFERENCE,
  GET_LIST,
  GET_ONE,
  CREATE,
  UPDATE,
  DELETE,
  DELETE_MANY
} from 'react-admin'
import diff from 'object-diff'
import axiosClient from '../config/axios'

function getIdKey ({resource, options}) {
  return (options[resource] && options[resource].id) || options.id || 'id'
}

export default (options = {}) => {
  const usePatch = !!options.usePatch
  const mapRequest = (type, resource, params) => {
    const idKey = getIdKey({resource, options})
    let query = {}

    let useFromData = false // Set true if there are files to upload
    switch (type) {
      case GET_MANY:
        let ids = params.ids || []
        query[idKey] = {$in: ids}
        query['$limit'] = ids.length
        return axiosClient().get(`/${resource}`, {
          params: query
        })
      case GET_MANY_REFERENCE:
        if (params.target && params.id) {
          query[params.target] = params.id
        }
      // eslint-disable-next-line no-fallthrough
      case GET_LIST:
        const {page, perPage} = params.pagination || {}
        const {field, order} = params.sort || {}
        const sortKey = `$sort[${field === 'id' ? idKey : field}]`
        let sortVal = order === 'DESC' ? -1 : 1
        if (perPage && page) {
          query['$limit'] = perPage
          query['$skip'] = perPage * (page - 1)
        }
        if (order) {
          query[sortKey] = JSON.stringify(sortVal)
        }
        Object.keys(params.filter).forEach(key => {
          if (options.filterObjectIdFields &&
            options.filterObjectIdFields[resource] &&
            options.filterObjectIdFields[resource][key]) {
            return
          }
          if (typeof params.filter[key] === 'string') {
            params.filter[key] = {
              $regex: `${params.filter[key]}`,
              $options: ''
            }
          }
        })
        Object.assign(query, params.filter)
        return axiosClient().get(`/${resource}`, {
          params: query
        })
      case GET_ONE:
        return axiosClient().get(`/${resource}/${params.id}`)
      case UPDATE:
        if (params.data.createdAt) {
          delete params.data.createdAt
        }

        // Check if there are any files to send. If yes we will need to use formData
        Object.keys(params.data).forEach((key) => {
          if (params.data[key] && params.data[key].rawFile && params.data[key].rawFile instanceof File) {
            params.data[key] = params.data[key].rawFile
            useFromData = true
          }

          if (params.data[key] && params.data[key].length) {
            Object.keys(params.data[key]).forEach((childKey) => {
              if (params.data[key][childKey] && params.data[key][childKey].rawFile && params.data[key][childKey].rawFile instanceof File) {
                params.data[key][childKey] = params.data[key][childKey].rawFile
                useFromData = true
              }
            })
          }
        })

        let data = params.data

        if (usePatch) {
          data = params.previousData ? diff(params.previousData, params.data) : params.data
        }

        if (useFromData) {
          let formData = new FormData()

          // Build formData
          Object.keys(data).forEach((key) => {
            if (data[key] instanceof File) {
              formData.append(key, data[key])
            } else {
              formData.append(key, JSON.stringify(data[key]))
            }
          })

          if (usePatch) {
            return axiosClient().patch(`/${resource}/${params.id}`, formData, {params: {kind: params.data.kind}})
          } else {
            return axiosClient().put(`/${resource}/${params.id}`, formData, {params: {kind: params.data.kind}})
          }
        }

        if (usePatch) {
          return axiosClient().patch(`/${resource}/${params.id}`, data, {params: {kind: params.data.kind}})
        } else {
          return axiosClient().put(`/${resource}/${params.id}`, data, {params: {kind: params.data.kind}})
        }
      case CREATE:
        // remove id from request
        delete params.data[options.id]

        Object.keys(params.data).forEach((key) => {
          if (params.data[key] && params.data[key].rawFile && params.data[key].rawFile instanceof File) {
            params.data[key] = params.data[key].rawFile
            useFromData = true
          }
        })

        if (useFromData) {
          // Build formData
          let formData = new FormData()
          Object.keys(params.data).forEach((key) => {
            if (params.data[key] instanceof File) {
              formData.append(key, params.data[key])
            } else {
              formData.append(key, JSON.stringify(params.data[key]))
            }
          })

          return axiosClient().post(`/${resource}`, formData)
        }

        return axiosClient().post(`/${resource}`, params.data)
      case DELETE:
        return axiosClient().delete(`/${resource}/${params.id}`)
      case DELETE_MANY:
        return axiosClient().delete(`/${resource}`, {params: { _id: {$in: params.ids} }})
      default:
        throw new Error(`Unsupported FeathersJS restClient action type ${type}`)
    }
  }

  const mapResponse = (response, type, resource, params) => {
    const idKey = getIdKey({resource, options})
    switch (type) {
      case GET_ONE:
      case UPDATE:
      case DELETE:
        return {data: {...response, id: response[idKey]}}
      case DELETE_MANY:
        let deletedIds = []
        response.forEach(item => {
          deletedIds.push(item._id)
        })
        return {data: deletedIds}
      case CREATE:
        return {data: {...params.data, id: response[idKey]}}
      case GET_MANY_REFERENCE: // fix GET_MANY_REFERENCE missing id
      case GET_MANY: // fix GET_MANY missing id
      case GET_LIST:
        return {
          data: response.data.map(item => {
            if (idKey !== 'id') {
              item.id = item[idKey]
            }
            return item
          }),
          total: response.total
        }
      default:
        return response
    }
  }

  return (type, resource, params) =>
    mapRequest(type, resource, params)
      .then(response => mapResponse(response.data, type, resource, params))
      .catch((err) => {
        if (err.response) {
          err.status = err.response.status
        }

        throw err
      })
}
